// pages/ContactRequestsPage.js
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore'
import { app } from '../firebase'
import toast from 'react-hot-toast'
import './ContactRequestsPage.css'

function ContactRequestsPage () {
  const { user } = useAuth()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all') // all, new, contacted, closed

  const db = getFirestore(app)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setError('Bitte melden Sie sich an')
      return
    }

    // Real-time listener for contact requests
    const requestsRef = collection(db, 'contactRequests')
    const q = query(requestsRef, orderBy('createdAt', 'desc'))

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const contactRequests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        setRequests(contactRequests)
        setLoading(false)
      },
      err => {
        console.error('Error loading contact requests:', err)
        setError('Fehler beim Laden der Kontaktanfragen')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [db, user])

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'contactRequests', requestId)
      await updateDoc(requestRef, {
        status: newStatus
      })
      toast.success('Status aktualisiert')
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error('Fehler beim Aktualisieren des Status')
    }
  }

  const handleDelete = async requestId => {
    if (
      !window.confirm(
        'Sind Sie sicher, dass Sie diese Anfrage löschen möchten?'
      )
    ) {
      return
    }

    try {
      const requestRef = doc(db, 'contactRequests', requestId)
      await deleteDoc(requestRef)
      toast.success('Anfrage gelöscht')
    } catch (error) {
      console.error('Error deleting request:', error)
      toast.error('Fehler beim Löschen der Anfrage')
    }
  }

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadgeClass = status => {
    switch (status) {
      case 'new':
        return 'status-new'
      case 'contacted':
        return 'status-contacted'
      case 'closed':
        return 'status-closed'
      default:
        return 'status-new'
    }
  }

  const getStatusLabel = status => {
    switch (status) {
      case 'new':
        return 'Neu'
      case 'contacted':
        return 'Kontaktiert'
      case 'closed':
        return 'Abgeschlossen'
      default:
        return 'Neu'
    }
  }

  const filteredRequests =
    filter === 'all'
      ? requests
      : requests.filter(req => req.status === filter)

  const stats = {
    total: requests.length,
    new: requests.filter(r => r.status === 'new').length,
    contacted: requests.filter(r => r.status === 'contacted').length,
    closed: requests.filter(r => r.status === 'closed').length
  }

  if (loading) {
    return (
      <div className='contact-requests-page'>
        <div className='loading'>Lädt Kontaktanfragen...</div>
      </div>
    )
  }

  return (
    <div className='contact-requests-page'>
      <div className='page-header'>
        <div>
          <h1>Kontaktanfragen</h1>
          <p className='page-subtitle'>
            Verwalten Sie Anfragen von der Landing Page
          </p>
        </div>
      </div>

      {error && (
        <div className='error-message'>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Stats */}
      <div className='stats-grid'>
        <div className='stat-card'>
          <div className='stat-value'>{stats.total}</div>
          <div className='stat-label'>Gesamt</div>
        </div>
        <div className='stat-card stat-new'>
          <div className='stat-value'>{stats.new}</div>
          <div className='stat-label'>Neu</div>
        </div>
        <div className='stat-card stat-contacted'>
          <div className='stat-value'>{stats.contacted}</div>
          <div className='stat-label'>Kontaktiert</div>
        </div>
        <div className='stat-card stat-closed'>
          <div className='stat-value'>{stats.closed}</div>
          <div className='stat-label'>Abgeschlossen</div>
        </div>
      </div>

      {/* Filter */}
      <div className='filter-bar'>
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Alle ({stats.total})
        </button>
        <button
          className={`filter-btn ${filter === 'new' ? 'active' : ''}`}
          onClick={() => setFilter('new')}
        >
          Neu ({stats.new})
        </button>
        <button
          className={`filter-btn ${filter === 'contacted' ? 'active' : ''}`}
          onClick={() => setFilter('contacted')}
        >
          Kontaktiert ({stats.contacted})
        </button>
        <button
          className={`filter-btn ${filter === 'closed' ? 'active' : ''}`}
          onClick={() => setFilter('closed')}
        >
          Abgeschlossen ({stats.closed})
        </button>
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <div className='empty-state'>
          <p>Keine Kontaktanfragen gefunden.</p>
        </div>
      ) : (
        <div className='requests-grid'>
          {filteredRequests.map(request => (
            <div key={request.id} className='request-card'>
              <div className='request-header'>
                <div className='request-meta'>
                  <h3>{request.company}</h3>
                  <span className='request-date'>
                    {formatDate(request.createdAt)}
                  </span>
                </div>
                <span
                  className={`status-badge ${getStatusBadgeClass(
                    request.status
                  )}`}
                >
                  {getStatusLabel(request.status)}
                </span>
              </div>

              <div className='request-body'>
                <div className='request-info'>
                  <div className='info-row'>
                    <span className='info-label'>Name:</span>
                    <span className='info-value'>{request.name}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>E-Mail:</span>
                    <a
                      href={`mailto:${request.email}`}
                      className='info-value email-link'
                    >
                      {request.email}
                    </a>
                  </div>
                </div>

                {request.message && (
                  <div className='request-message'>
                    <span className='message-label'>Nachricht:</span>
                    <p>{request.message}</p>
                  </div>
                )}
              </div>

              <div className='request-actions'>
                <select
                  value={request.status || 'new'}
                  onChange={e => handleStatusChange(request.id, e.target.value)}
                  className='status-select'
                >
                  <option value='new'>Neu</option>
                  <option value='contacted'>Kontaktiert</option>
                  <option value='closed'>Abgeschlossen</option>
                </select>

                <button
                  className='btn-delete'
                  onClick={() => handleDelete(request.id)}
                  title='Anfrage löschen'
                >
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ContactRequestsPage
